import { useEffect, useMemo, useState } from "react";
import { useOutletContext, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Package,
  TrendingUp,
  DollarSign,
  Crown,
  RefreshCw,
  Inbox,
  Search,
  Eye,
  Gift,
  Repeat2,
  Box,
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { fmtMoney, fmtDateTime } from "@/lib/intl";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

function fmtDate(v) {
  return fmtDateTime(v);
}

function StatCard({ label, value, hint, icon: Icon, testId }) {
  return (
    <Card
      data-testid={testId}
      className="p-6 rounded-lg border border-zinc-200 shadow-none hover:border-zinc-300 transition-colors bg-white"
    >
      <div className="flex items-start justify-between">
        <div className="text-2xs sm:text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500">
          {label}
        </div>
        {Icon && <Icon className="h-4 w-4 text-zinc-400" strokeWidth={1.75} />}
      </div>
      <div className="mt-4 text-3xl sm:text-4xl font-medium tracking-tight text-zinc-950 font-mono-num">
        {value}
      </div>
      {hint && <div className="mt-1 text-xs text-zinc-500 truncate">{hint}</div>}
    </Card>
  );
}

function productKindFromRecord(p) {
  if (p.delisted) return { label: "delisted", icon: Box };
  // Acuity products have category / type fields. Use heuristic.
  const t = String(p.type || p.category || "").toLowerCase();
  if (t.includes("certificate") || p.giftCertificate || p.isGiftCertificate)
    return { label: "gift cert", icon: Gift };
  if (t.includes("subscription") || p.subscription)
    return { label: "subscription", icon: Repeat2 };
  if (t.includes("package") || t.includes("class") || t.includes("session"))
    return { label: "package", icon: Package };
  return { label: t || "product", icon: Box };
}

export default function Products() {
  const { status } = useOutletContext();
  const [data, setData] = useState({ products: [], summary: null });
  const [loading, setLoading] = useState(false);
  const [hasFetched, setHasFetched] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState({ key: "revenue", dir: "desc" });
  const [selected, setSelected] = useState(null);

  async function fetchData() {
    if (!status.configured) return;
    setLoading(true);
    setError(null);
    try {
      const r = await api.get("/products/stats");
      setData(r.data || { products: [], summary: null });
      setHasFetched(true);
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message;
      setError(msg);
      toast.error("Failed to load products", { description: msg });
    } finally {
      setLoading(false);
    }
  }

  // No auto-fetch on mount — wait for the user to click Apply.

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return data.products;
    return data.products.filter((p) =>
      [p.name, p.description, p.id].some((x) =>
        String(x || "").toLowerCase().includes(q)
      )
    );
  }, [data.products, search]);

  const sorted = useMemo(() => {
    const arr = [...filtered];
    arr.sort((a, b) => {
      const av = a?.[sort.key];
      const bv = b?.[sort.key];
      if (av == null) return 1;
      if (bv == null) return -1;
      const an = Number(av);
      const bn = Number(bv);
      if (!Number.isNaN(an) && !Number.isNaN(bn)) {
        return sort.dir === "asc" ? an - bn : bn - an;
      }
      return sort.dir === "asc"
        ? String(av).localeCompare(String(bv))
        : String(bv).localeCompare(String(av));
    });
    return arr;
  }, [filtered, sort]);

  const maxRev = useMemo(
    () => Math.max(1, ...sorted.map((p) => Number(p.revenue || 0))),
    [sorted]
  );

  function toggleSort(key) {
    setSort((s) =>
      s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "desc" }
    );
  }

  if (!status.configured) {
    return (
      <Card className="p-12 rounded-lg border-2 border-dashed border-zinc-200 bg-zinc-50 text-center">
        <h2 className="font-display text-2xl font-semibold tracking-tight text-zinc-950">
          Connect Acuity to view products
        </h2>
        <p className="mt-2 text-sm text-zinc-600">
          Add your User ID and API Key in Settings to load your catalog.
        </p>
        <Link to="/settings">
          <Button
            className="mt-5 bg-zinc-950 hover:bg-zinc-800 text-white"
            data-testid="products-goto-settings"
          >
            Open Settings
          </Button>
        </Link>
      </Card>
    );
  }

  const summary = data.summary || {};

  return (
    <div className="space-y-8" data-testid="products-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500">
            Catalog
          </div>
          <h1 className="mt-2 font-display text-4xl sm:text-5xl font-bold tracking-tight text-zinc-950">
            Products
          </h1>
          <p className="mt-2 text-sm text-zinc-600 max-w-xl leading-relaxed">
            Every product, package and gift certificate in your Acuity store, joined with
            order data to show what's actually selling.
          </p>
        </div>
        <Button
          onClick={fetchData}
          data-testid="products-refresh-btn"
          className="bg-zinc-950 hover:bg-zinc-800 text-white self-start"
        >
          <RefreshCw className={cn("h-4 w-4 mr-1.5", loading && "animate-spin")} />
          Refresh
        </Button>
      </div>

      {error && (
        <div
          data-testid="products-error"
          className="p-4 rounded-md border border-red-200 bg-red-50 text-sm text-red-700"
        >
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          label="Catalog size"
          value={loading ? "—" : summary.catalog_count ?? 0}
          hint="Active products in Acuity"
          icon={Package}
          testId="stat-catalog-count"
        />
        <StatCard
          label="Units sold"
          value={loading ? "—" : summary.total_units_sold ?? 0}
          hint="Across recent orders"
          icon={TrendingUp}
          testId="stat-units-sold"
        />
        <StatCard
          label="Product revenue"
          value={loading ? "—" : fmtMoney(summary.total_revenue)}
          hint="From product line items"
          icon={DollarSign}
          testId="stat-product-revenue"
        />
        <StatCard
          label="Top seller"
          value={loading ? "—" : fmtMoney(summary.top_seller_revenue)}
          hint={summary.top_seller_name || "—"}
          icon={Crown}
          testId="stat-top-seller"
        />
      </div>

      <Card className="p-4 sm:p-5 rounded-lg border border-zinc-200 shadow-none">
        <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
          <div className="relative w-full sm:w-72">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" />
            <Input
              data-testid="products-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name or id…"
              className="pl-9 border-zinc-300 focus-visible:ring-zinc-950"
            />
          </div>
          <div
            className="text-xs text-zinc-500 font-mono-num"
            data-testid="products-row-count"
          >
            {sorted.length} products
          </div>
        </div>
      </Card>

      <Card className="rounded-lg border border-zinc-200 shadow-none overflow-hidden">
        <div className="overflow-x-auto thin-scroll">
          <Table>
            <TableHeader>
              <TableRow className="bg-zinc-50 hover:bg-zinc-50">
                {[
                  ["name", "Product"],
                  ["price", "Price"],
                  ["units_sold", "Units"],
                  ["revenue", "Revenue"],
                  ["last_sold_at", "Last sold"],
                ].map(([key, label]) => (
                  <TableHead
                    key={key}
                    onClick={() => toggleSort(key)}
                    data-testid={`products-sort-${key}`}
                    className="cursor-pointer select-none text-xs font-semibold uppercase tracking-wider text-zinc-600"
                  >
                    {label}
                    {sort.key === key && (
                      <span className="ml-1 text-zinc-400">
                        {sort.dir === "asc" ? "▲" : "▼"}
                      </span>
                    )}
                  </TableHead>
                ))}
                <TableHead className="text-xs font-semibold uppercase tracking-wider text-zinc-600 w-20">
                  Details
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && (
                <TableRow>
                  <TableCell colSpan={6} className="py-12 text-center text-sm text-zinc-500">
                    Loading…
                  </TableCell>
                </TableRow>
              )}
              {!loading && sorted.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} className="py-16">
                    <div className="flex flex-col items-center justify-center text-center text-zinc-500">
                      <Inbox className="h-7 w-7 mb-2 text-zinc-300" />
                      <div className="text-sm">
                        {hasFetched
                          ? "No products found"
                          : "Click Apply (or refresh) to load product stats."}
                      </div>
                    </div>
                  </TableCell>
                </TableRow>
              )}
              {!loading &&
                sorted.map((p) => {
                  const kind = productKindFromRecord(p);
                  const KindIcon = kind.icon;
                  const pct = (Number(p.revenue || 0) / maxRev) * 100;
                  return (
                    <TableRow key={p.id || p.name} data-testid={`product-row-${p.id || p.name}`}>
                      <TableCell className="py-3">
                        <div className="flex items-center gap-3">
                          <div className="h-9 w-9 grid place-items-center rounded-md bg-zinc-100 border border-zinc-200 text-zinc-700 shrink-0">
                            <KindIcon className="h-4 w-4" strokeWidth={1.75} />
                          </div>
                          <div className="min-w-0">
                            <div className="text-zinc-950 font-medium truncate">
                              {p.name || `Product #${p.id}`}
                            </div>
                            <div className="text-xs text-zinc-500 font-mono-num flex items-center gap-2">
                              <span>#{p.id}</span>
                              <Badge
                                variant="outline"
                                className="border-zinc-200 bg-zinc-50 text-zinc-600 text-2xs px-1.5 py-0 h-4"
                              >
                                {kind.label}
                              </Badge>
                              {p.delisted && (
                                <Badge
                                  variant="outline"
                                  className="border-amber-200 bg-amber-50 text-amber-700 text-2xs px-1.5 py-0 h-4"
                                >
                                  not in catalog
                                </Badge>
                              )}
                            </div>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="font-mono-num text-sm text-zinc-900">
                        {p.price ? fmtMoney(p.price) : "—"}
                      </TableCell>
                      <TableCell className="font-mono-num text-sm text-zinc-900">
                        {p.units_sold || 0}
                      </TableCell>
                      <TableCell className="min-w-[180px]">
                        <div className="font-mono-num text-sm text-zinc-950">
                          {fmtMoney(p.revenue)}
                        </div>
                        <div className="mt-1 h-1 w-full bg-zinc-100 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-zinc-950"
                            style={{ width: `${Math.min(100, Math.max(0, pct))}%` }}
                          />
                        </div>
                      </TableCell>
                      <TableCell className="font-mono-num text-sm text-zinc-700">
                        {fmtDate(p.last_sold_at)}
                      </TableCell>
                      <TableCell>
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-zinc-300 hover:bg-zinc-100 h-8 px-2"
                          onClick={() => setSelected(p)}
                          data-testid={`view-product-${p.id || p.name}`}
                        >
                          <Eye className="h-3.5 w-3.5 mr-1" /> View
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </div>
      </Card>

      <ProductDetails
        product={selected}
        open={!!selected}
        onOpenChange={(o) => !o && setSelected(null)}
      />
    </div>
  );
}

function ProductDetails({ product, open, onOpenChange }) {
  if (!product) return null;
  const kind = productKindFromRecord(product);
  const KindIcon = kind.icon;

  const stats = [
    ["Units sold", product.units_sold ?? 0],
    ["Revenue", fmtMoney(product.revenue)],
    ["Orders", product.orders_count ?? 0],
    ["Last sold", fmtDate(product.last_sold_at)],
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="product-details-dialog"
        className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col"
      >
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 grid place-items-center rounded-md bg-zinc-100 border border-zinc-200 text-zinc-700">
              <KindIcon className="h-5 w-5" strokeWidth={1.75} />
            </div>
            <div>
              <DialogTitle className="font-display text-2xl tracking-tight">
                {product.name || `Product #${product.id}`}
              </DialogTitle>
              <DialogDescription className="font-mono-num text-xs">
                #{product.id} · {kind.label}
                {product.price ? ` · ${fmtMoney(product.price)}` : ""}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <Tabs defaultValue="overview" className="flex-1 overflow-hidden flex flex-col">
          <TabsList className="bg-zinc-100">
            <TabsTrigger value="overview" data-testid="product-tab-overview">
              Overview
            </TabsTrigger>
            <TabsTrigger value="raw" data-testid="product-tab-raw">
              Raw JSON
            </TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="overflow-y-auto pr-1 thin-scroll">
            <div className="space-y-6 mt-4">
              <section>
                <div className="text-2xs font-semibold uppercase tracking-[0.2em] text-zinc-500 mb-2">
                  Sales
                </div>
                <div className="grid grid-cols-2 gap-3">
                  {stats.map(([label, value]) => (
                    <div
                      key={label}
                      className="border border-zinc-200 rounded-md p-3 bg-white"
                    >
                      <div className="text-2xs font-semibold uppercase tracking-wider text-zinc-500">
                        {label}
                      </div>
                      <div className="mt-1 font-mono-num text-lg text-zinc-950">
                        {value}
                      </div>
                    </div>
                  ))}
                </div>
              </section>

              {product.description && (
                <section>
                  <div className="text-2xs font-semibold uppercase tracking-[0.2em] text-zinc-500 mb-2">
                    Description
                  </div>
                  <div className="text-sm text-zinc-700 leading-relaxed border border-zinc-200 rounded-md p-4 bg-white whitespace-pre-wrap">
                    {product.description}
                  </div>
                </section>
              )}
            </div>
          </TabsContent>

          <TabsContent value="raw" className="overflow-y-auto pr-1 thin-scroll">
            <pre
              data-testid="product-raw-json"
              className="mt-4 text-xs font-mono-num bg-zinc-950 text-zinc-100 p-4 rounded-md overflow-auto"
            >
              {JSON.stringify(product, null, 2)}
            </pre>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
